import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Button,
    CircularProgress,
    Tabs,
    Tab,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Sync as SyncIcon,
    Apartment as ApartmentIcon,
    DesktopWindows as DesktopWindowsIcon,
    Router as RouterIcon,
    NotificationsActive as NotificationsActiveIcon,
    Tune as TuneIcon,
    Cloud as CloudIcon,
    Campaign as CampaignIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { useQuery } from '@tanstack/react-query';
import JsdhNotifikaceService from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import {
    useUpdateAdminKonfiguraceMutation,
    useTriggerRefreshMutation,
    useJednotkaQuery,
    useUpdateJednotkaMutation,
    useUploadLogoMutation,
    useDeleteLogoMutation,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { Konfigurace } from '../../api/JsdhNotifikaceService/generated';
import type { JednotkaDto } from '../../api/JsdhNotifikaceService/generated/models/JednotkaDto';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { PoplachConfig } from '../../api/JsdhNotifikaceService/generated';
import { DEFAULT_POPLACH_CONFIG, parsePoplachConfig } from '../../api/JsdhNotifikaceService/generated';

import JednotkaTab from './configuration/JednotkaTab';
import DisplejTab from './configuration/DisplejTab';
import OstatniTab from './configuration/OstatniTab';
import ZarizeniTab from './configuration/ZarizeniTab';
import NotifikaceTab from './configuration/NotifikaceTab';
import MeteoTab from './configuration/MeteoTab';
import NastenkaTab from './configuration/NastenkaTab';
import EmailNastaveniTab from './configuration/EmailNastaveniTab';

type ConfigTab = 'jednotka' | 'displej' | 'ostatni' | 'zarizeni' | 'notifikace' | 'meteo' | 'nastenka' | 'email';

const ConfigurationPage: React.FC = () => {
    const { isAdmin } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Tab state
    const [activeTab, setActiveTab] = useState<ConfigTab>('jednotka');

    // Feature flagy modulů (HO-84) — meteo a nástěnka jako tab v konfiguraci
    const { enabled: meteoFlagEnabled } = useFeatureFlag('MODUL_METEO');
    const { enabled: nastenkaFlagEnabled } = useFeatureFlag('MODUL_NASTENKA');
    // HO-22: e-mailové notifikace jako gated modul
    const { enabled: emailFlagEnabled } = useFeatureFlag('MODUL_EMAIL');

    // Data queries
    const {
        data: konfigurace,
        isLoading: isLoadingKonfigurace,
        refetch: refetchKonfigurace,
    } = useQuery<Konfigurace[]>({
        queryKey: ['konfigurace'],
        queryFn: () => JsdhNotifikaceService.getAllKonfigurace(),
    });

    const {
        data: jednotka,
        isLoading: isLoadingJednotka,
        refetch: refetchJednotka,
    } = useJednotkaQuery();

    // Mutations
    const updateKonfiguraceMutation = useUpdateAdminKonfiguraceMutation();
    const refreshMutation = useTriggerRefreshMutation();
    const updateJednotkaMutation = useUpdateJednotkaMutation();
    const uploadLogoMutation = useUploadLogoMutation();
    const deleteLogoMutation = useDeleteLogoMutation();

    // Jednotka form state
    const [jednotkaForm, setJednotkaForm] = useState<JednotkaDto>({
        nazev: '',
    });

    // Poplach display config state
    const [editingDisplayConfig, setEditingDisplayConfig] = useState<PoplachConfig>(DEFAULT_POPLACH_CONFIG);
    const [displayConfigDirty, setDisplayConfigDirty] = useState(false);
    const [displayConfigLoaded, setDisplayConfigLoaded] = useState(false);

    // Initialize jednotka form when data loads
    useEffect(() => {
        if (jednotka) {
            setJednotkaForm(jednotka);
        }
    }, [jednotka]);

    // Initialize poplach display config from konfigurace
    useEffect(() => {
        if (konfigurace && !displayConfigLoaded) {
            const existing = konfigurace.find(k => k.klic === 'POPLACH_DISPLAY_CONFIG');
            setEditingDisplayConfig(parsePoplachConfig(existing?.hodnota));
            setDisplayConfigLoaded(true);
        }
    }, [konfigurace, displayConfigLoaded]);

    // Safety guard: reset tab for non-admin users
    useEffect(() => {
        if (!isAdmin && activeTab !== 'jednotka') {
            setActiveTab('jednotka');
        } else if (activeTab === 'meteo' && !meteoFlagEnabled) {
            setActiveTab('jednotka');
        } else if (activeTab === 'nastenka' && !nastenkaFlagEnabled) {
            setActiveTab('jednotka');
        } else if (activeTab === 'email' && !emailFlagEnabled) {
            setActiveTab('jednotka');
        }
    }, [isAdmin, activeTab, meteoFlagEnabled, nastenkaFlagEnabled, emailFlagEnabled]);

    const handleDisplayConfigChange = (updater: (prev: PoplachConfig) => PoplachConfig) => {
        setEditingDisplayConfig(updater);
        setDisplayConfigDirty(true);
    };

    const handleSaveDisplayConfig = () => {
        updateKonfiguraceMutation.mutate(
            { klic: 'POPLACH_DISPLAY_CONFIG', hodnota: JSON.stringify(editingDisplayConfig) },
            {
                onSuccess: () => {
                    setDisplayConfigDirty(false);
                    refetchKonfigurace();
                },
            }
        );
    };

    // Refresh dialog state
    const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);

    const handleTriggerRefresh = () => {
        refreshMutation.mutate(undefined, {
            onSuccess: () => setRefreshDialogOpen(false),
        });
    };

    const isLoading = isLoadingKonfigurace || isLoadingJednotka;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title="Konfigurace"
                action={
                    isAdmin ? (
                        <Tooltip title="Odešle signál připojeným displejům k obnovení obrazovky">
                            <Button
                                variant="outlined"
                                startIcon={<SyncIcon />}
                                onClick={() => setRefreshDialogOpen(true)}
                            >
                                Obnovit poplachové displeje
                            </Button>
                        </Tooltip>
                    ) : undefined
                }
            />

            {/* Tab navigation (admin only — non-admin sees only Jednotka content) */}
            {isAdmin && (
                <Paper sx={{ mb: 3 }}>
                    <Tabs
                        value={
                            (activeTab === 'meteo' && !meteoFlagEnabled) ||
                            (activeTab === 'nastenka' && !nastenkaFlagEnabled) ||
                            (activeTab === 'email' && !emailFlagEnabled)
                                ? 'jednotka'
                                : activeTab
                        }
                        onChange={(_, v) => setActiveTab(v)}
                        orientation={isMobile ? 'vertical' : 'horizontal'}
                        variant={isMobile ? 'fullWidth' : 'fullWidth'}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab value="jednotka" label="Jednotka" icon={<ApartmentIcon />} iconPosition="start" />
                        <Tab value="displej" label="Displej" icon={<DesktopWindowsIcon />} iconPosition="start" />
                        <Tab value="ostatni" label="Ostatní" icon={<TuneIcon />} iconPosition="start" />
                        <Tab value="zarizeni" label="Zařízení" icon={<RouterIcon />} iconPosition="start" />
                        <Tab value="notifikace" label="Notifikace" icon={<NotificationsActiveIcon />} iconPosition="start" />
                        {meteoFlagEnabled && <Tab value="meteo" label="Meteo" icon={<CloudIcon />} iconPosition="start" />}
                        {nastenkaFlagEnabled && <Tab value="nastenka" label="Nástěnka" icon={<CampaignIcon />} iconPosition="start" />}
                        {emailFlagEnabled && <Tab value="email" label="E-mail" icon={<EmailIcon />} iconPosition="start" />}
                    </Tabs>
                </Paper>
            )}

            {/* Tab content */}
            {activeTab === 'jednotka' && (
                <JednotkaTab
                    jednotka={jednotka}
                    jednotkaForm={jednotkaForm}
                    setJednotkaForm={setJednotkaForm}
                    isAdmin={isAdmin}
                    updateJednotkaMutation={updateJednotkaMutation}
                    uploadLogoMutation={uploadLogoMutation}
                    deleteLogoMutation={deleteLogoMutation}
                    refetchJednotka={refetchJednotka}
                />
            )}

            {activeTab === 'displej' && isAdmin && (
                <DisplejTab
                    updateKonfiguraceMutation={updateKonfiguraceMutation}
                    editingDisplayConfig={editingDisplayConfig}
                    handleDisplayConfigChange={handleDisplayConfigChange}
                    displayConfigDirty={displayConfigDirty}
                    handleSaveDisplayConfig={handleSaveDisplayConfig}
                />
            )}

            {activeTab === 'ostatni' && isAdmin && (
                <OstatniTab
                    konfigurace={konfigurace}
                    updateKonfiguraceMutation={updateKonfiguraceMutation}
                    refetchKonfigurace={refetchKonfigurace}
                />
            )}

            {activeTab === 'zarizeni' && isAdmin && (
                <ZarizeniTab />
            )}

            {activeTab === 'notifikace' && isAdmin && (
                <NotifikaceTab
                    konfigurace={konfigurace}
                    updateKonfiguraceMutation={updateKonfiguraceMutation}
                    refetchKonfigurace={refetchKonfigurace}
                />
            )}

            {activeTab === 'meteo' && isAdmin && meteoFlagEnabled && (
                <MeteoTab />
            )}

            {activeTab === 'nastenka' && isAdmin && nastenkaFlagEnabled && (
                <NastenkaTab
                    konfigurace={konfigurace}
                    updateKonfiguraceMutation={updateKonfiguraceMutation}
                    refetchKonfigurace={refetchKonfigurace}
                />
            )}

            {activeTab === 'email' && isAdmin && emailFlagEnabled && (
                <EmailNastaveniTab
                    konfigurace={konfigurace}
                    updateKonfiguraceMutation={updateKonfiguraceMutation}
                    refetchKonfigurace={refetchKonfigurace}
                />
            )}

            {/* Refresh confirmation dialog */}
            <Dialog open={refreshDialogOpen} onClose={() => setRefreshDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Obnovit poplachové displeje</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Odešle signál všem připojeným Raspberry Pi displejům, aby znovu načetly konfiguraci
                        a obnovily poplachovou obrazovku. Použijte po změně nastavení displeje nebo device tokenů.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefreshDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={handleTriggerRefresh}
                        disabled={refreshMutation.isPending}
                    >
                        {refreshMutation.isPending ? 'Odesílám...' : 'Obnovit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ConfigurationPage;
